/**
 * Pinia store for managing support ticket categories.
 *
 * Handles fetching, creating, updating, and deactivating ticket categories.
 * Categories are used to classify tickets and are managed by employees.
 */
import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';
import { shouldThrottle } from '@/util/rateLimit.js';
import posthog from '@/composables/usePostHog.js';

export const useTicketCategoryStore = defineStore('ticketCategoryStore', {
	state: () => ({
		/** @type {Array} All ticket categories */
		categories: [],
		/** @type {boolean} Whether categories have been loaded */
		categoriesLoaded: false,
		/** @type {boolean} Loading flag */
		loading: false,
		/** @type {string|null} Error message */
		error: null
	}),
	actions: {
		/**
		 * Fetches ticket categories.
		 * @param {Object} [options={}] - Options: { force: boolean, includeInactive: boolean }
		 * @returns {Promise<Array>} The list of categories
		 */
		async fetchCategories(options = {}) {
			const { force = false, includeInactive = false } = options;
			if (!force && this.categoriesLoaded) {
				return this.categories;
			}
			if (shouldThrottle('fetchCategories', 100)) {
				posthog.capture('throttled_action', { action: 'fetchCategories' });
				return this.categories;
			}
			this.loading = true;
			this.error = null;
			try {
				const endpoint = includeInactive ? '/ticket-categories/all' : '/ticket-categories/active';
				const { data } = await apiClient.get(endpoint);
				this.categories = data || [];
				this.categoriesLoaded = true;
				return this.categories;
			} catch (e) {
				this.error = e.response?.data?.error || 'Failed to fetch categories';
				this.categories = [];
				return [];
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Creates a new ticket category (employee only).
		 * @param {Object} category - Category data: { name, description }
		 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
		 */
		async createCategory(category) {
			if (shouldThrottle('createCategory', 100)) {
				posthog.capture('throttled_action', { action: 'createCategory' });
				return { success: false, message: 'Please wait before creating again.' };
			}
			try {
				const { data } = await apiClient.post('/ticket-categories', category);
				this.categories.push(data);
				this.categoriesLoaded = false;
				return { success: true, data };
			} catch (e) {
				return { success: false, message: e.response?.data?.error || 'Failed to create category' };
			}
		},

		/**
		 * Updates an existing ticket category (employee only).
		 * @param {number} categoryNumber - The category number
		 * @param {Object} updates - Fields to update: { name, description }
		 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
		 */
		async updateCategory(categoryNumber, updates) {
			if (shouldThrottle('updateCategory', 100)) {
				posthog.capture('throttled_action', { action: 'updateCategory' });
				return { success: false, message: 'Please wait before updating again.' };
			}
			try {
				const { data } = await apiClient.patch(`/ticket-categories/${categoryNumber}`, updates);
				const index = this.categories.findIndex((c) => c.categoryNumber === categoryNumber);
				if (index !== -1) {
					this.categories[index] = data;
				}
				return { success: true, data };
			} catch (e) {
				return { success: false, message: e.response?.data?.error || 'Failed to update category' };
			}
		},

		/**
		 * Deactivates (soft-deletes) a ticket category (employee only).
		 * @param {number} categoryNumber - The category number to deactivate
		 * @returns {Promise<{success: boolean, message?: string}>}
		 */
		async deactivateCategory(categoryNumber) {
			if (shouldThrottle('deactivateCategory', 100)) {
				posthog.capture('throttled_action', { action: 'deactivateCategory' });
				return { success: false, message: 'Please wait before deactivating again.' };
			}
			try {
				await apiClient.delete(`/ticket-categories/${categoryNumber}`);
				const index = this.categories.findIndex((c) => c.categoryNumber === categoryNumber);
				if (index !== -1) {
					this.categories[index].active = false;
				}
				return { success: true };
			} catch (e) {
				return { success: false, message: e.response?.data?.error || 'Failed to deactivate category' };
			}
		}
	}
});
